import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  getTicketStats,
  getAdminTickets,
  getAdminTicket,
  adminReplyToTicket,
  updateTicketStatus,
  assignTicket,
  updateTicketPriority
} from '../services/adminSupportService';
import './AdminSupportDashboard.css';

const CATEGORY_LABELS = {
  billing: 'Billing',
  technical: 'Technical',
  account: 'Account',
  coaching: 'Coaching',
  nutrition: 'Nutrition',
  general: 'General'
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_on_customer: 'Awaiting Reply',
  resolved: 'Resolved',
  closed: 'Closed'
};

const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

const AdminSupportDashboard = () => {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedToMe, setAssignedToMe] = useState(false);

  // Detail panel
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadTickets();
  }, [statusFilter, categoryFilter, priorityFilter, assignedToMe, page]);

  const loadStats = async () => {
    try {
      const data = await getTicketStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminTickets({
        status: statusFilter,
        category: categoryFilter,
        priority: priorityFilter,
        assignedToMe,
        page
      });
      setTickets(data.tickets);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, priorityFilter, assignedToMe, page]);

  const openTicket = async (ticketId) => {
    setDetailLoading(true);
    try {
      const data = await getAdminTicket(ticketId);
      setSelectedTicket(data.ticket);
      setReplyText('');
      removeScreenshot();
    } catch (err) {
      toast.error('Failed to load ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    setReplySending(true);
    try {
      const data = await adminReplyToTicket(selectedTicket._id, { message: replyText, screenshot });
      setSelectedTicket(data.ticket);
      setReplyText('');
      removeScreenshot();
      toast.success('Reply sent');
      loadTickets();
      loadStats();
    } catch (err) {
      toast.error(err.message || 'Failed to send reply');
    } finally {
      setReplySending(false);
    }
  };

  const handleStatusChange = async (status) => {
    if (!selectedTicket) return;
    try {
      const data = await updateTicketStatus(selectedTicket._id, status);
      setSelectedTicket(data.ticket);
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      loadTickets();
      loadStats();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (priority) => {
    if (!selectedTicket) return;
    try {
      const data = await updateTicketPriority(selectedTicket._id, priority);
      setSelectedTicket(data.ticket);
      toast.success(`Priority updated to ${PRIORITY_LABELS[priority]}`);
      loadTickets();
    } catch (err) {
      toast.error('Failed to update priority');
    }
  };

  const handleAssignToMe = async () => {
    if (!selectedTicket) return;
    try {
      const data = await assignTicket(selectedTicket._id);
      setSelectedTicket(data.ticket);
      toast.success('Ticket assigned to you');
      loadTickets();
    } catch (err) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are allowed');
      return;
    }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return '—';
    const hours = Math.round(ms / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h`;
    return `${Math.round(hours / 24)}d`;
  };

  return (
    <div className="admin-support">
      <div className="admin-support-container">
        <div className="admin-support-header">
          <h1>Support Dashboard</h1>
          <p>Manage support tickets and user requests</p>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="support-stats-bar">
            <div className="stat-card stat-open">
              <span className="stat-value">{stats.openCount}</span>
              <span className="stat-label">Open</span>
            </div>
            <div className="stat-card stat-progress">
              <span className="stat-value">{stats.inProgressCount}</span>
              <span className="stat-label">In Progress</span>
            </div>
            <div className="stat-card stat-waiting">
              <span className="stat-value">{stats.waitingCount}</span>
              <span className="stat-label">Waiting</span>
            </div>
            <div className="stat-card stat-today">
              <span className="stat-value">{stats.todayCount}</span>
              <span className="stat-label">Today</span>
            </div>
            <div className="stat-card stat-avg">
              <span className="stat-value">{formatDuration(stats.avgResolutionMs)}</span>
              <span className="stat-label">Avg Resolution</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="admin-support-filters">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="waiting_on_customer">Awaiting Reply</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
            <option value="all">All Categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}>
            <option value="all">All Priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <label className="filter-checkbox">
            <input type="checkbox" checked={assignedToMe} onChange={(e) => { setAssignedToMe(e.target.checked); setPage(1); }} />
            Assigned to me
          </label>
          <span className="admin-ticket-count">{total} ticket{total !== 1 ? 's' : ''}</span>
        </div>

        <div className="admin-support-layout">
          {/* Ticket List */}
          <div className="admin-ticket-list">
            {loading ? (
              <div className="support-loading"><div className="loading-spinner" /><p>Loading...</p></div>
            ) : tickets.length === 0 ? (
              <div className="admin-empty">No tickets match your filters</div>
            ) : (
              tickets.map((ticket) => (
                <div
                  key={ticket._id}
                  className={`admin-ticket-row ${selectedTicket?._id === ticket._id ? 'active' : ''}`}
                  onClick={() => openTicket(ticket._id)}
                >
                  <div className="row-top">
                    <span className="row-number">{ticket.ticketNumber}</span>
                    <span className={`ticket-priority priority-${ticket.priority}`}>
                      {PRIORITY_LABELS[ticket.priority]}
                    </span>
                  </div>
                  <div className="row-subject">{ticket.subject}</div>
                  <div className="row-meta">
                    <span className="row-user">{ticket.userName}</span>
                    <span className={`ticket-status status-${ticket.status}`}>
                      {STATUS_LABELS[ticket.status]}
                    </span>
                    <span className="row-date">{formatDate(ticket.updatedAt)}</span>
                  </div>
                </div>
              ))
            )}

            {totalPages > 1 && (
              <div className="support-pagination">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span>{page}/{totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="admin-ticket-detail">
            {detailLoading ? (
              <div className="support-loading"><div className="loading-spinner" /></div>
            ) : !selectedTicket ? (
              <div className="detail-empty">
                <p>Select a ticket to view details</p>
              </div>
            ) : (
              <>
                <div className="detail-header">
                  <h2>{selectedTicket.subject}</h2>
                  <div className="detail-meta">
                    <span className="ticket-number">{selectedTicket.ticketNumber}</span>
                    <span className="detail-user">{selectedTicket.userName} ({selectedTicket.userEmail})</span>
                    <span className="detail-tier">Tier: {selectedTicket.userSubscriptionTier}</span>
                  </div>
                </div>

                {/* Admin Controls */}
                <div className="detail-controls">
                  <div className="control-group">
                    <label>Status</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div className="control-group">
                    <label>Priority</label>
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => handlePriorityChange(e.target.value)}
                    >
                      {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <button className="assign-btn" onClick={handleAssignToMe}>
                    Assign to Me
                  </button>
                </div>

                {/* Messages */}
                <div className="detail-messages">
                  {selectedTicket.messages.map((msg, i) => (
                    <div key={msg._id || i} className={`detail-message role-${msg.senderRole}`}>
                      <div className="dmsg-header">
                        <span className="dmsg-sender">{msg.senderName}</span>
                        <span className={`message-role-badge role-badge-${msg.senderRole}`}>
                          {msg.senderRole}
                        </span>
                        <span className="dmsg-time">{formatDate(msg.createdAt)}</span>
                      </div>
                      <div className="dmsg-body">{msg.body}</div>
                      {msg.attachments?.length > 0 && (
                        <div className="dmsg-attachments">
                          {msg.attachments.map((att, j) => (
                            <a key={j} href={att.url} target="_blank" rel="noopener noreferrer">
                              <img src={att.url} alt={att.filename} />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Reply */}
                {!['closed', 'resolved'].includes(selectedTicket.status) && (
                  <form className="detail-reply" onSubmit={handleReply}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type admin reply..."
                      rows={3}
                      maxLength={5000}
                    />
                    <div className="detail-reply-actions">
                      <div className="reply-left">
                        {screenshotPreview ? (
                          <div className="reply-screenshot-preview">
                            <img src={screenshotPreview} alt="Preview" />
                            <button type="button" onClick={removeScreenshot}>✕</button>
                          </div>
                        ) : (
                          <label className="reply-attach-btn">
                            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleScreenshotChange} hidden />
                            📷 Attach
                          </label>
                        )}
                      </div>
                      <button type="submit" className="reply-send-btn" disabled={replySending || !replyText.trim()}>
                        {replySending ? 'Sending...' : 'Send Reply'}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSupportDashboard;
